import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import { Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useAuthStore } from '../../store/authStore';

// Component for managing slideshow images in admin panel
const AdminSlideshow = () => {
  const { user } = useAuthStore();
  const [slides, setSlides] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load slides from API
  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      // Пытаемся получить слайды с удаленного VPS
      try {
        const vpsResponse = await axios.get('/api/settings/slides', config);
        if (vpsResponse.data && vpsResponse.data.success && Array.isArray(vpsResponse.data.data)) {
          console.log('Slides loaded from VPS:', vpsResponse.data.data);
          setSlides(vpsResponse.data.data);
          return;
        }
      } catch (vpsError) {
        console.warn('Failed to load slides from VPS, falling back to local API:', vpsError);
      }
      
      // Если не получилось загрузить с VPS, пробуем локальный API
      const response = await axios.get('/api/slides', config);
      setSlides(response.data);
    } catch (err) {
      setError('Не удалось загрузить изображения слайдшоу');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (slide = null) => {
    setCurrentSlide(slide || { title: '', image: '', order: slides.length + 1 });
    setImagePreview(slide ? slide.image : '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSlide(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSlide(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSlide = async () => {
    if (!currentSlide.title) {
      setSnackbar({
        open: true,
        message: 'Пожалуйста, введите название слайда',
        severity: 'error'
      });
      return;
    }

    if (!imageFile && !currentSlide.image) {
      setSnackbar({
        open: true,
        message: 'Пожалуйста, выберите изображение',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('title', currentSlide.title);
      formData.append('order', currentSlide.order);
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      let response;
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      };
      
      if (currentSlide.id) {
        // Пытаемся обновить слайд на удаленном VPS
        try {
          response = await axios.put(`/api/settings/slides/${currentSlide.id}`, formData, { headers });
          if (response.data && response.data.success) {
            console.log('Slide updated on VPS successfully');
            
            // Обновляем локальный список слайдов
            await fetchSlides();
            
            setSnackbar({
              open: true,
              message: 'Слайд успешно обновлен на VPS',
              severity: 'success'
            });
            
            handleCloseDialog();
            return;
          }
        } catch (vpsError) {
          console.warn('Failed to update slide on VPS, falling back to local API:', vpsError);
        }
        
        // Если не получилось обновить на VPS, пробуем локальный API
        response = await axios.put(`/api/slides/${currentSlide.id}`, formData, { headers });
        
        setSlides(slides.map(slide => 
          slide.id === currentSlide.id ? response.data : slide
        ));
        
        setSnackbar({
          open: true,
          message: 'Слайд успешно обновлен локально',
          severity: 'success'
        });
      } else {
        // Пытаемся создать новый слайд на удаленном VPS
        try {
          response = await axios.post('/api/settings/slides', formData, { headers });
          if (response.data && response.data.success) {
            console.log('Slide created on VPS successfully');
            
            // Обновляем локальный список слайдов
            await fetchSlides();
            
            setSnackbar({
              open: true,
              message: 'Слайд успешно создан на VPS',
              severity: 'success'
            });
            
            handleCloseDialog();
            return;
          }
        } catch (vpsError) {
          console.warn('Failed to create slide on VPS, falling back to local API:', vpsError);
        }
        
        // Если не получилось создать на VPS, пробуем локальный API
        response = await axios.post('/api/slides', formData, { headers });
        
        setSlides([...slides, response.data]);
        
        setSnackbar({
          open: true,
          message: 'Слайд успешно создан локально',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving slide:', err);
      setSnackbar({
        open: true,
        message: `Ошибка: ${err.response?.data?.message || 'Не удалось сохранить слайд'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlide = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Пытаемся удалить слайд на удаленном VPS
      try {
        const vpsResponse = await axios.delete(`/api/settings/slides/${id}`, { headers });
        if (vpsResponse.data && vpsResponse.data.success) {
          console.log('Slide deleted from VPS successfully');
          
          // Обновляем локальный список слайдов
          await fetchSlides();
          
          setSnackbar({
            open: true,
            message: 'Слайд успешно удален с VPS',
            severity: 'success'
          });
          return;
        }
      } catch (vpsError) {
        console.warn('Failed to delete slide from VPS, falling back to local API:', vpsError);
      }
      
      // Если не получилось удалить на VPS, пробуем локальный API
      await axios.delete(`/api/slides/${id}`, { headers });
      setSlides(slides.filter(slide => slide.id !== id));
      setSnackbar({
        open: true,
        message: 'Слайд успешно удален локально',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting slide:', err);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить слайд',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Управление слайдшоу
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить слайд
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {slides.length > 0 ? (
            slides
              .sort((a, b) => a.order - b.order)
              .map((slide) => (
                <Grid item xs={12} sm={6} md={4} key={slide.id}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={slide.image}
                      alt={slide.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {slide.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Порядок: {slide.order}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog(slide)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteSlide(slide.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Нет слайдов
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Добавьте слайды для отображения на главной странице
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add/Edit Slide Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentSlide?.id ? 'Редактировать слайд' : 'Добавить новый слайд'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Название"
              name="title"
              value={currentSlide?.title || ''}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Порядок"
              name="order"
              type="number"
              value={currentSlide?.order || ''}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              InputProps={{ inputProps: { min: 1 } }}
            />
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Изображение
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="slide-image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="slide-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                >
                  Выбрать изображение
                </Button>
              </label>
            </Box>
            
            {(imagePreview || currentSlide?.image) && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Предпросмотр
                </Typography>
                <Box
                  component="img"
                  src={imagePreview || currentSlide?.image}
                  alt="Preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    mt: 1,
                    border: '1px solid #eee'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Отмена
          </Button>
          <Button 
            onClick={handleSaveSlide} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert 
          elevation={6} 
          variant="filled" 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default AdminSlideshow;
